import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Wallet, TrendingUp, ArrowDownToLine, Crown, CreditCard, XCircle, Banknote } from "lucide-react";

const KIND_LABELS = {
  market_sale: "Vente Market",
  invoice_payment: "Facture payée",
  withdrawal: "Retrait",
  withdrawal_refund: "Remboursement retrait",
};
const WD_STATUS = { demande: "En attente", approuvee: "Approuvée", payee: "Payée", rejetee: "Rejetée" };
const WD_STYLES = {
  demande: "bg-amber-50 text-amber-700",
  approuvee: "bg-blue-50 text-blue-700",
  payee: "bg-green-50 text-green-700",
  rejetee: "bg-red-50 text-red-600",
};

export default function WalletPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    setError("");
    try {
      const [w, t, wd] = await Promise.all([
        api.get("/wallet"),
        api.get("/wallet/transactions"),
        api.get("/wallet/withdrawals"),
      ]);
      setWallet(w.data);
      setTxns(t.data);
      setWithdrawals(wd.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const subscribe = async () => {
    setError("");
    setSubscribing(true);
    try {
      const res = await api.post("/wallet/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.approval_url;
    } catch (err) {
      setError(formatApiError(err));
      setSubscribing(false);
    }
  };

  const cancelSub = async () => {
    setError("");
    setNotice("");
    try {
      const res = await api.post("/wallet/subscription/cancel");
      setNotice(`Abonnement annulé — accès Pro conservé jusqu'au ${res.data.current_period_end?.slice(0, 10)}.`);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const requestWithdrawal = async () => {
    setError("");
    setNotice("");
    try {
      await api.post("/wallet/withdrawals", { amount: parseFloat(amount) });
      setAmount("");
      setNotice("Demande de retrait enregistrée. Notre équipe la traite manuellement (virement ou PayPal).");
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user) return null;

  const pct = wallet ? Math.round((wallet.commission_rate ?? 0.1) * 10000) / 100 : 10;

  return (
    <div data-testid="wallet-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
            <Wallet className="w-7 h-7 text-brand-turquoise" />
            NEXA Wallet
          </h1>
          <p className="text-white/70 text-sm mt-1">
            Vos revenus de ventes et de factures, vos retraits et votre abonnement Pro.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div data-testid="wallet-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}
        {notice && (
          <div data-testid="wallet-notice" className="text-sm text-brand-navy bg-brand-turquoise/10 border border-brand-turquoise/30 rounded-lg px-4 py-3">{notice}</div>
        )}

        {!wallet ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Solde disponible</span>
                  <Wallet className="w-4 h-4 text-brand-turquoise" />
                </div>
                <p data-testid="wallet-balance" className="text-3xl font-extrabold text-brand-navy">{wallet.balance} $</p>
                <p className="text-xs text-slate-400 mt-1">{wallet.currency} — retirable sur demande</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total gagné</span>
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                </div>
                <p data-testid="wallet-total-earned" className="text-2xl font-bold text-slate-900">{wallet.total_earned} $</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total retiré</span>
                  <ArrowDownToLine className="w-4 h-4 text-slate-400" />
                </div>
                <p data-testid="wallet-total-withdrawn" className="text-2xl font-bold text-slate-900">{wallet.total_withdrawn} $</p>
              </div>
            </div>

            <div data-testid="wallet-commission-info" className="text-sm text-slate-600 bg-brand-sky/50 border border-brand-sky rounded-lg px-4 py-3">
              Commission plateforme actuelle : <strong>{pct} %</strong> — prélevée sur chaque vente Market et chaque facture payée en ligne avant crédit de votre portefeuille. Le détail brut/commission/net figure dans chaque transaction ci-dessous.
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div data-testid="pro-card" className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-brand-orange" />
                  <h2 className="font-bold text-brand-navy">NEXA Pro</h2>
                  {wallet.is_pro && (
                    <span data-testid="pro-status" className="text-xs font-semibold bg-brand-orange/10 text-brand-orange rounded-full px-2.5 py-0.5">Actif</span>
                  )}
                </div>
                <p className="text-2xl font-extrabold text-brand-navy">{wallet.pro_price} $ <span className="text-sm font-normal text-slate-500">CAD / {wallet.pro_period_days} jours</span></p>
                <ul className="text-sm text-slate-600 mt-3 space-y-1.5 list-disc list-inside">
                  <li>Badge Pro sur votre profil</li>
                  <li>{wallet.pro_credits} crédits de publication d'offre par période</li>
                </ul>
                {wallet.is_pro ? (
                  <div className="mt-4 space-y-2">
                    <p data-testid="pro-renewal" className="text-sm text-slate-600">
                      Renouvellement : {wallet.subscription?.current_period_end?.slice(0, 10)}
                    </p>
                    <button
                      data-testid="pro-cancel-button"
                      onClick={cancelSub}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-4 py-2 hover:bg-red-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Annuler l'abonnement
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="pro-subscribe-button"
                    onClick={subscribe}
                    disabled={subscribing}
                    className="mt-4 inline-flex items-center gap-2 bg-brand-blue text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors disabled:opacity-50"
                  >
                    <CreditCard className="w-4 h-4" />
                    {subscribing ? "Redirection vers PayPal…" : "Passer à Pro avec PayPal"}
                  </button>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Banknote className="w-5 h-5 text-brand-turquoise" />
                  <h2 className="font-bold text-brand-navy">Retraits</h2>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Demandez un retrait de votre solde — traitement manuel par notre équipe (virement ou PayPal).
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="withdrawal-amount-input"
                    type="number" min="0.01" step="0.01" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Montant (CAD)"
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
                  />
                  <button
                    data-testid="withdrawal-submit-button"
                    onClick={requestWithdrawal}
                    disabled={!amount || parseFloat(amount) <= 0}
                    className="bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors disabled:opacity-40"
                  >
                    Demander
                  </button>
                </div>
                <div className="mt-4 space-y-2">
                  {withdrawals.length === 0 ? (
                    <p data-testid="withdrawals-empty" className="text-xs text-slate-400">Aucune demande de retrait.</p>
                  ) : (
                    withdrawals.map((w) => (
                      <div key={w.request_id} data-testid={`withdrawal-row-${w.request_id}`} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                        <span className="text-brand-navy font-medium">{w.amount} $ {w.currency}</span>
                        <span data-testid={`withdrawal-status-${w.request_id}`} className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${WD_STYLES[w.status]}`}>
                          {WD_STATUS[w.status]}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="font-semibold text-brand-navy">Grand livre des transactions</h2>
              </div>
              {txns.length === 0 ? (
                <p data-testid="transactions-empty" className="px-5 py-10 text-center text-sm text-slate-400">
                  Aucune transaction — vos ventes Market et factures payées apparaîtront ici.
                </p>
              ) : (
                txns.map((t) => (
                  <div key={t.txn_id} data-testid={`txn-row-${t.txn_id}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-slate-50">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${
                      t.direction === "credit" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                    }`}>
                      {KIND_LABELS[t.kind] || t.kind}
                    </span>
                    <p className="flex-1 min-w-[220px] text-xs text-slate-600">{t.note}</p>
                    <span className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString("fr-CA") : ""}</span>
                    <span data-testid={`txn-amount-${t.txn_id}`} className={`text-sm font-bold ${t.direction === "credit" ? "text-green-600" : "text-red-600"}`}>
                      {t.direction === "credit" ? "+" : "−"}{t.amount} $
                    </span>
                    <span className="text-xs text-slate-400 w-20 text-right">solde {t.balance_after} $</span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
